import {
  ColumnDataTypes,
  FilterOptionTypes,
  type ColumnConfig,
} from '@/components/DataTable';
import {
  StringOperatorsWithEmpty,
  EnumOperatorsWithEmpty,
  NumberOperatorsWithEmpty,
  DateOperatorsWithEmpty,
  ToolNameCell,
  CategoryCell,
  PidCell,
  PortCell,
  StartedAtCell,
  ToolNameFilterOption,
  RunningNowCategoryFilterOption,
} from '@/components/shared/shadow-ai';
import type { PersonalRunningNowListItem } from '@kirin/api-client/contracts/account-shadow-ai-running-now';
import { PERSONAL_RUNNING_NOW_FILTERABLE_FIELDS } from '@kirin/api-client/contracts/account-shadow-ai-running-now';
import { getShadowAiCategoryLabel } from '@kirin/api-client/contracts/shadow-ai';
import { PERSONAL_RUNNING_NOW_FILTER_URL_KEYS } from './constants';
import { fetchPersonalRunningNowFilterOptions } from './api';
import { TABLE_IDS } from '@/components/DataTable/tableIds';

const FILTER_OPTIONS_KEY = `${TABLE_IDS.PERSONAL.SHADOW_AI.RUNNING}-filterOptions`;

export function createPersonalRunningNowColumns(): ColumnConfig<PersonalRunningNowListItem>[] {
  return [
    {
      id: 'name',
      header: 'Process',
      accessor: 'toolName',
      renderCell: ({ row }) => <ToolNameCell row={row} />,
      width: 220,
      sortable: true,
      sortField: 'name',
      sortKey: 'n',
      filterable: true,
      dataType: ColumnDataTypes.STRING,
      filterOperators: StringOperatorsWithEmpty,
      statePersistence: {
        urlKey:
          PERSONAL_RUNNING_NOW_FILTER_URL_KEYS[
            PERSONAL_RUNNING_NOW_FILTERABLE_FIELDS.NAME
          ],
      },
      filterOptions: {
        type: FilterOptionTypes.DYNAMIC,
        getQueryKey: params => [FILTER_OPTIONS_KEY, 'name', params] as const,
        queryFn: params => fetchPersonalRunningNowFilterOptions('name', params),
        pagination: { mode: 'offset', limit: 10 },
        queryConfig: { staleTime: 2 * 60 * 1000 },
        render: (option, isSelected) => (
          <ToolNameFilterOption name={option.value} isSelected={isSelected} />
        ),
      },
    },
    {
      id: 'category',
      header: 'Category',
      accessor: 'category',
      renderCell: ({ row }) => <CategoryCell row={row} />,
      width: 160,
      sortable: true,
      sortField: 'category',
      sortKey: 'cat',
      filterable: true,
      dataType: ColumnDataTypes.ENUM,
      filterOperators: EnumOperatorsWithEmpty,
      statePersistence: {
        urlKey:
          PERSONAL_RUNNING_NOW_FILTER_URL_KEYS[
            PERSONAL_RUNNING_NOW_FILTERABLE_FIELDS.CATEGORY
          ],
      },
      filterOptions: {
        type: FilterOptionTypes.DYNAMIC,
        getQueryKey: params =>
          [FILTER_OPTIONS_KEY, 'category', params] as const,
        queryFn: params =>
          fetchPersonalRunningNowFilterOptions('category', params),
        pagination: { mode: 'offset', limit: 10 },
        queryConfig: { staleTime: 2 * 60 * 1000 },
        labelFormatter: getShadowAiCategoryLabel,
        render: (option, isSelected) => (
          <RunningNowCategoryFilterOption
            category={option.value}
            isSelected={isSelected}
          />
        ),
      },
    },
    {
      id: 'pid',
      header: 'PID',
      accessor: 'pid',
      renderCell: ({ row }) => <PidCell row={row} />,
      width: 100,
      sortable: true,
      sortField: 'pid',
      sortKey: 'pid',
      filterable: true,
      dataType: ColumnDataTypes.NUMBER,
      filterOperators: NumberOperatorsWithEmpty,
      statePersistence: {
        urlKey:
          PERSONAL_RUNNING_NOW_FILTER_URL_KEYS[
            PERSONAL_RUNNING_NOW_FILTERABLE_FIELDS.PID
          ],
      },
      filterOptions: {
        type: FilterOptionTypes.STATIC,
        options: [],
      },
    },
    {
      id: 'port',
      header: 'Port',
      accessor: 'port',
      renderCell: ({ row }) => <PortCell row={row} />,
      // KIR-5589: 90 split "Port" mid-word next to the filter/sort icons.
      width: 110,
      sortable: true,
      sortField: 'port',
      sortKey: 'prt',
      filterable: true,
      dataType: ColumnDataTypes.NUMBER,
      filterOperators: NumberOperatorsWithEmpty,
      statePersistence: {
        urlKey:
          PERSONAL_RUNNING_NOW_FILTER_URL_KEYS[
            PERSONAL_RUNNING_NOW_FILTERABLE_FIELDS.PORT
          ],
      },
      filterOptions: {
        type: FilterOptionTypes.STATIC,
        options: [],
      },
    },
    {
      id: 'startedAt',
      header: 'Running For',
      accessor: 'startedAt',
      renderCell: ({ row }) => <StartedAtCell row={row} />,
      // Fits the longest duration ("less than a minute") next to the icon
      // under the widest system-ui fallback (DejaVu Sans) without clipping.
      width: 180,
      sortable: true,
      sortField: 'startedAt',
      sortKey: 'sa',
      filterable: true,
      dataType: ColumnDataTypes.DATE,
      filterOperators: DateOperatorsWithEmpty,
      statePersistence: {
        urlKey:
          PERSONAL_RUNNING_NOW_FILTER_URL_KEYS[
            PERSONAL_RUNNING_NOW_FILTERABLE_FIELDS.STARTED_AT
          ],
      },
      filterOptions: {
        type: FilterOptionTypes.STATIC,
        options: [],
      },
    },
  ];
}
